import React, { useState, useEffect } from 'react';
import { getOrgUsers, getOrgProgress, getMyOrg, joinOrg, OrgSummary, OrgMember, MemberProgress } from '../api/public';
import { isOrgAdmin } from '../auth/cognito';

const MODULE_LABELS: Record<string, string> = {
  quest_chapter_1: 'Quest — Ch.1',
  quest_chapter_2: 'Quest — Ch.2',
  quest_chapter_3: 'Quest — Ch.3',
  algorithm_mirror: 'Algorithm Mirror',
  data_heist: 'Data Heist',
};

const card: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 16,
  padding: '20px 24px',
};

function pill(color: string, text: string) {
  return (
    <span style={{ background: `${color}20`, border: `1px solid ${color}40`, color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 100 }}>
      {text}
    </span>
  );
}

// ── Join org view (for users not yet in an org) ────────────────────────────

function JoinOrgView({ onJoined }: { onJoined: () => void }) {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    const res = await joinOrg(code.trim());
    setLoading(false);
    if (res.success) {
      setStatus({ type: 'success', msg: `Joined ${res.orgName}! Reload to see your team.` });
      setTimeout(onJoined, 1500);
    } else {
      setStatus({ type: 'error', msg: res.error || 'Invalid code.' });
    }
  }

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 420, width: '100%' }}>
        <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 20 }}>🏢</div>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#e2e8f0', textAlign: 'center', marginBottom: 8 }}>Join your team</h2>
        <p style={{ fontSize: 14, color: '#94a3b8', textAlign: 'center', marginBottom: 32 }}>
          Enter the 6-character code your organisation admin shared with you.
        </p>
        <form onSubmit={handleJoin} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <input
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="AB3X7K"
            maxLength={6}
            required
            style={{
              background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 12, padding: '14px 18px', color: '#fff', fontSize: 22,
              fontWeight: 800, letterSpacing: '0.2em', textAlign: 'center', outline: 'none',
              fontFamily: 'monospace',
            }}
          />
          {status && (
            <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, background: status.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${status.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`, color: status.type === 'success' ? '#22c55e' : '#ef4444' }}>
              {status.msg}
            </div>
          )}
          <button type="submit" disabled={loading || code.length < 6} style={{
            background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none', borderRadius: 10,
            color: '#fff', fontSize: 15, fontWeight: 700, padding: '13px 24px',
            cursor: loading || code.length < 6 ? 'not-allowed' : 'pointer',
            opacity: loading || code.length < 6 ? 0.5 : 1,
          }}>
            {loading ? 'Joining...' : 'Join team'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Org admin portal ───────────────────────────────────────────────────────

export default function Portal() {
  const orgAdmin = isOrgAdmin();
  const [org, setOrg] = useState<OrgSummary | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [progress, setProgress] = useState<MemberProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  async function load() {
    setLoading(true);
    const [meRes, usersRes, progressRes] = await Promise.all([
      getMyOrg(),
      orgAdmin ? getOrgUsers() : Promise.resolve([]),
      orgAdmin ? getOrgProgress() : Promise.resolve([]),
    ]);
    setOrg(meRes.org);
    setRole(meRes.role);
    setMembers(usersRes);
    setProgress(progressRes);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  function copyJoinCode() {
    if (!org?.joinCode) return;
    navigator.clipboard.writeText(org.joinCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 36, height: 36, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#818cf8', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Not in an org yet — show join flow
  if (!org || !orgAdmin) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a', fontFamily: "'Segoe UI',system-ui,sans-serif", color: '#fff' }}>
        <JoinOrgView onJoined={load} />
      </div>
    );
  }

  // Org exists but payment hasn't been completed
  if (org.status === 'pending_payment') {
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a', fontFamily: "'Segoe UI',system-ui,sans-serif", color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 20 }}>💳</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#e2e8f0', margin: '0 0 12px' }}>Payment required</h2>
          <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7, margin: '0 0 8px' }}>
            Your organisation's subscription hasn't been activated yet.
          </p>
          <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.7, margin: '0 0 32px' }}>
            Please contact your platform administrator at{' '}
            <a href="https://admin.privacy.education" style={{ color: '#818cf8' }}>admin.privacy.education</a>{' '}
            to complete the payment and activate your org.
          </p>
          <a
            href="https://admin.privacy.education"
            style={{ display: 'inline-block', padding: '12px 28px', borderRadius: 12, fontSize: 14, fontWeight: 600, background: '#7c3aed', color: '#fff', textDecoration: 'none' }}
          >
            Go to Admin Portal
          </a>
        </div>
      </div>
    );
  }

  // Build per-member progress summary
  const progressByUser: Record<string, { modules: string[]; totalXP: number }> = {};
  for (const p of progress) {
    if (!progressByUser[p.userId]) progressByUser[p.userId] = { modules: [], totalXP: 0 };
    progressByUser[p.userId].modules.push(p.module);
    progressByUser[p.userId].totalXP += p.xpEarned;
  }

  const totalCompletions = progress.length;
  const activeMembers = members.filter(m => progressByUser[m.userId]);
  const planColor = org.plan === 'enterprise' ? '#a78bfa' : org.plan === 'professional' ? '#60a5fa' : '#34d399';
  const statusColor = org.status === 'active' ? '#22c55e' : org.status === 'trial' ? '#f97316' : '#ef4444';
  const seatsPct = org.seats === -1 ? 0 : Math.min(100, (org.seatsUsed / org.seats) * 100);

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', fontFamily: "'Segoe UI',system-ui,sans-serif", color: '#fff' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '32px 20px' }}>

        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0 }}>{org.name}</h1>
            {pill(planColor, org.plan.charAt(0).toUpperCase() + org.plan.slice(1))}
            {pill(statusColor, org.status.charAt(0).toUpperCase() + org.status.slice(1))}
          </div>
          <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>Team portal</p>
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 14, marginBottom: 28 }}>
          {[
            { label: 'Members', value: members.length, color: '#818cf8' },
            { label: 'Active learners', value: activeMembers.length, color: '#34d399' },
            { label: 'Module completions', value: totalCompletions, color: '#818cf8' },
            { label: org.seats === -1 ? 'Seats' : `Seats used`, value: org.seats === -1 ? '∞' : `${org.seatsUsed} / ${org.seats}`, color: seatsPct > 90 ? '#ef4444' : '#818cf8' },
          ].map((s, i) => (
            <div key={i} style={card}>
              <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Join code card */}
        <div style={{ ...card, marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#818cf8', marginBottom: 6, fontWeight: 700 }}>Team join code</div>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '0.2em', fontFamily: 'monospace', color: '#e2e8f0' }}>{org.joinCode}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Share this with your team — they enter it at public.privacy.education/portal</div>
          </div>
          <button onClick={copyJoinCode} style={{
            background: copied ? 'rgba(34,197,94,0.15)' : 'rgba(99,102,241,0.15)',
            border: `1px solid ${copied ? 'rgba(34,197,94,0.4)' : 'rgba(99,102,241,0.4)'}`,
            color: copied ? '#22c55e' : '#a5b4fc',
            borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>
            {copied ? 'Copied!' : 'Copy code'}
          </button>
        </div>

        {/* Team progress table */}
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#818cf8', marginBottom: 14, fontWeight: 700 }}>Team progress</div>
        {members.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', padding: 48, color: 'rgba(255,255,255,0.3)' }}>
            No members yet. Share the join code above to invite your team.
          </div>
        ) : (
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  {['Member', 'Joined', 'Modules completed', 'XP earned', 'Status'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {members.map((m, i) => {
                  const prog = progressByUser[m.userId];
                  const moduleCount = prog?.modules.length || 0;
                  const xpTotal = prog?.totalXP || 0;
                  const completedLabels = (prog?.modules || []).map(mod => MODULE_LABELS[mod] || mod);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#e2e8f0' }}>{m.email}</td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>{new Date(m.joinedAt).toLocaleDateString()}</td>
                      <td style={{ padding: '12px 16px' }}>
                        {moduleCount === 0
                          ? <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)' }}>None yet</span>
                          : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {completedLabels.map(l => (
                                <span key={l} style={{ fontSize: 11, background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc', padding: '2px 8px', borderRadius: 6 }}>{l}</span>
                              ))}
                            </div>
                        }
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 700, color: xpTotal > 0 ? '#34d399' : 'rgba(255,255,255,0.2)' }}>{xpTotal > 0 ? `${xpTotal} XP` : '—'}</td>
                      <td style={{ padding: '12px 16px' }}>{pill(m.status === 'active' ? '#22c55e' : '#ef4444', m.status)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
